import _ from 'lodash';
import { useEffect, useCallback, useRef, useState } from 'react';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { proactiveCareyManager } from 'services/ProactiveCareyService';
import { useCPPContext } from '../components/Patient/CPP/CPPContext';
import { store } from 'careand-redux/store';

export function useProactiveCarey(
  patientId: string | undefined,
  appointmentId: string | undefined,
) {
  const dispatch = useAppDispatch();
  const [isActive, setIsActive] = useState(false);
  const { unreactiveNoteValue } = useCPPContext();
  // Identifies this consumer to the manager: several components can be mounted for
  // the same appointment, and each may only tear down the poller it asked for.
  const ownerId = useRef(_.uniqueId('proactive-carey-')).current;

  useEffect(() => {
    if (!appointmentId || !patientId) return;

    if (isActive) {
      // Start polling for this appointment
      // Use store.getState instead of capturing state in closure
      proactiveCareyManager.startForAppointment(
        patientId,
        appointmentId,
        unreactiveNoteValue,
        dispatch,
        () => store.getState(),
        ownerId,
      );
    } else {
      proactiveCareyManager.stopForAppointment(appointmentId, ownerId);
    }

    // Cleanup on unmount. The inactive branch needs it too: runOnce() can build a
    // service (and a poller) for a consumer that never called start().
    return () => {
      proactiveCareyManager.stopForAppointment(appointmentId, ownerId);
    };
  }, [appointmentId, dispatch, isActive, ownerId, patientId, unreactiveNoteValue]);

  const start = useCallback(() => setIsActive(true), []);
  const stop = useCallback(() => setIsActive(false), []);

  const runOnce = useCallback(
    (instructions?: string) => {
      if (!appointmentId || !patientId) {
        return;
      }
      proactiveCareyManager.runOnceForAppointment(
        instructions,
        patientId,
        appointmentId,
        unreactiveNoteValue,
        dispatch,
        () => store.getState(),
      );
    },
    [appointmentId, dispatch, patientId, unreactiveNoteValue],
  );

  return { start, stop, runOnce };
}
